import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Loader2,
  PauseCircle,
  Play,
  RefreshCw,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

type MonitoringCadence = "default" | "daily" | "weekly";
type ScrapeStatus =
  | "pending"
  | "running"
  | "submitted"
  | "succeeded"
  | "failed"
  | "rejected";
type ProductTab = "scrapes" | "reviews";

type Product = {
  id: string;
  asin: string;
  productTitle: string | null;
  catalogSource: string;
  monitoringEnabled: boolean;
  monitoringCadence: MonitoringCadence;
  effectiveCadence: "daily" | "weekly";
  addedAt: string;
  lastScrapedAt: string | null;
  lastScheduledAt: string | null;
  lastCompletedAt: string | null;
  nextRunAt: string | null;
  isDue: boolean;
  amazonUrl: string;
};

type ProductDetail = {
  product: Product;
  brand: {
    id: string;
    name: string;
    brandRegistry: boolean;
    collaboratorStatus: string;
  };
  customer: {
    id: string;
    email: string;
    organizationName: string;
  };
  summary: {
    totalScrapes: number;
    successfulScrapes: number;
    failedScrapes: number;
    manualScrapes: number;
    reviewCount: number;
    flaggedCount: number;
    lastSuccessfulAt: string | null;
  };
  scrapes: Array<{
    id: string;
    source: "auto" | "human";
    status: ScrapeStatus;
    asin: string;
    coverage: string | null;
    auditRequestId: string | null;
    auditRequest: {
      id: string;
      status: string;
      scrapedReviewCount: number;
      flaggedCount: number;
      createdAt: string;
      updatedAt: string;
    } | null;
    manualScraper: { id: string; email: string; name: string | null } | null;
    errorCategory: string | null;
    errorMessage: string | null;
    notes: string | null;
    createdAt: string;
    startedAt: string | null;
    claimedAt: string | null;
    submittedAt: string | null;
    qaedAt: string | null;
    completedAt: string | null;
    reviewCount: number;
    flaggedCount: number;
  }>;
  recentReviews: Array<{
    id: string;
    auditRequest: { id: string; createdAt: string; status: string };
    amazonReviewId: string;
    reviewUrl: string | null;
    rating: number;
    title: string | null;
    body: string;
    reviewerName: string | null;
    verifiedPurchase: boolean;
    reviewDate: string | null;
    createdAt: string;
    classification: {
      flaggedForTakedown: boolean;
      violations: { type?: string }[] | null;
      rationale: string | null;
      actionStatus: string;
      submittedAt: string | null;
      removedAt: string | null;
    } | null;
  }>;
};

function formatDateTime(value: string | null | undefined) {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function statusClass(status: ScrapeStatus) {
  if (status === "succeeded") return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (status === "failed" || status === "rejected") return "bg-red-50 text-red-700 ring-red-200";
  if (status === "running" || status === "submitted") return "bg-sky-50 text-sky-700 ring-sky-200";
  return "bg-slate-100 text-slate-700 ring-slate-200";
}

function Pill({
  children,
  className,
}: {
  children: React.ReactNode;
  className: string;
}) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${className}`}>
      {children}
    </span>
  );
}

function Tile({
  label,
  value,
  detail,
}: {
  label: string;
  value: string | number;
  detail?: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 text-slate-900">
      <div className="text-xs font-medium uppercase text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      {detail && <div className="mt-1 text-xs text-slate-500">{detail}</div>}
    </div>
  );
}

function AdminProductDetailInner() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [acting, setActing] = useState(false);
  const tab: ProductTab = searchParams.get("tab") === "reviews" ? "reviews" : "scrapes";

  function setTab(nextTab: ProductTab) {
    if (nextTab === tab) return;
    const next = new URLSearchParams(searchParams);
    if (nextTab === "scrapes") next.delete("tab");
    else next.set("tab", nextTab);
    setSearchParams(next, { replace: false });
  }

  async function refresh() {
    if (!id) return;
    setLoading(true);
    setError(null);
    const res = await api<ProductDetail>(`/admin/products/${id}`);
    setLoading(false);
    if (!res.ok || !res.data) {
      setData(null);
      setError(res.error ?? "Could not load product.");
      return;
    }
    setData(res.data);
  }

  useEffect(() => {
    void refresh();
  }, [id]);

  async function updateMonitoring(payload: Partial<{
    monitoringEnabled: boolean;
    monitoringCadence: MonitoringCadence;
  }>) {
    if (!data) return;
    setActing(true);
    setError(null);
    setMessage(null);
    const res = await api(`/admin/products/${data.product.id}/monitoring`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    setActing(false);
    if (!res.ok) {
      setError(res.error ?? "Could not update monitoring.");
      return;
    }
    setMessage("Monitoring updated.");
    void refresh();
  }

  async function runNow() {
    if (!data) return;
    setActing(true);
    setError(null);
    setMessage(null);
    const res = await api<{ auditRequestId: string; path: string }>(
      `/admin/products/${data.product.id}/run-now`,
      { method: "POST", body: JSON.stringify({}) }
    );
    setActing(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Could not start monitoring run.");
      return;
    }
    setMessage(`Started ${res.data.path}. Audit ${res.data.auditRequestId}.`);
    void refresh();
  }

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading product...
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <Link to="/admin/customers" className="text-sm text-slate-500 hover:text-slate-900">
          Back to customers
        </Link>
        <p className="mt-4 text-sm text-red-600" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!data) return null;

  const { product, brand, customer, summary, scrapes, recentReviews } = data;

  return (
    <div>
      <Link
        to={`/admin/customers/${customer.id}`}
        className="text-sm text-slate-500 hover:text-slate-900"
      >
        Back to {customer.organizationName}
      </Link>

      <section className="mt-4 rounded-2xl border border-slate-200 bg-white p-5 text-slate-900">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              {customer.organizationName} / {brand.name}
            </div>
            <h1 className="mt-1 font-mono text-2xl font-semibold tracking-tight">
              {product.asin}
            </h1>
            {product.productTitle && (
              <p className="mt-1 max-w-4xl truncate text-sm text-slate-600">
                {product.productTitle}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Pill
                className={
                  product.monitoringEnabled
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : "bg-slate-100 text-slate-700 ring-slate-200"
                }
              >
                {product.monitoringEnabled ? "Monitored" : "Paused"}
              </Pill>
              <Pill className="bg-slate-100 text-slate-700 ring-slate-200">
                {product.monitoringCadence === "default"
                  ? `Default (${product.effectiveCadence})`
                  : product.monitoringCadence}
              </Pill>
              {product.isDue && (
                <Pill className="bg-amber-50 text-amber-700 ring-amber-200">
                  Due
                </Pill>
              )}
              <a
                href={product.amazonUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900"
              >
                Open Amazon <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <select
              value={product.monitoringCadence}
              onChange={(event) =>
                updateMonitoring({
                  monitoringCadence: event.target.value as MonitoringCadence,
                })
              }
              disabled={acting}
              className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm"
            >
              <option value="default">Default ({product.effectiveCadence})</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
            <Button type="button" size="sm" variant="outline" onClick={runNow} disabled={acting}>
              {acting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Run now
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={acting}
              onClick={() =>
                updateMonitoring({
                  monitoringEnabled: !product.monitoringEnabled,
                })
              }
            >
              {product.monitoringEnabled ? (
                <PauseCircle className="h-4 w-4" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              {product.monitoringEnabled ? "Pause" : "Resume"}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={refresh} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="mt-5 grid gap-3 text-sm text-slate-600 md:grid-cols-4">
          <div>
            Last completed: {formatDateTime(product.lastCompletedAt ?? product.lastScrapedAt)}
          </div>
          <div>Last scheduled: {formatDateTime(product.lastScheduledAt)}</div>
          <div>Next run: {product.monitoringEnabled ? formatDateTime(product.nextRunAt) : "-"}</div>
          <div>Catalog source: {product.catalogSource}</div>
        </div>

        {message && (
          <p className="mt-4 flex items-center gap-2 text-sm text-emerald-700" role="status">
            <CheckCircle2 className="h-4 w-4" /> {message}
          </p>
        )}
        {error && (
          <p className="mt-4 flex items-center gap-2 text-sm text-red-600" role="alert">
            <AlertTriangle className="h-4 w-4" /> {error}
          </p>
        )}
      </section>

      <section className="mt-5 grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Tile label="Scrapes" value={summary.totalScrapes} />
        <Tile label="Succeeded" value={summary.successfulScrapes} />
        <Tile label="Failed" value={summary.failedScrapes} />
        <Tile label="Manual" value={summary.manualScrapes} />
        <Tile label="Reviews" value={summary.reviewCount} />
        <Tile label="Flagged" value={summary.flaggedCount} />
      </section>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 text-sm">
        <div className="flex gap-2">
          {(["scrapes", "reviews"] as const).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setTab(item)}
              className={
                tab === item
                  ? "border-b-2 border-slate-900 px-3 py-2 font-medium text-slate-900"
                  : "px-3 py-2 text-slate-500 hover:text-slate-900"
              }
            >
              {item === "scrapes" ? "Scrapes" : `Recent reviews (${recentReviews.length})`}
            </button>
          ))}
        </div>
        <Link
          to={`/admin/products/${product.id}/reviews`}
          className="mb-2 inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
        >
          All reviews <ExternalLink className="h-3.5 w-3.5" />
        </Link>
      </div>

      {tab === "scrapes" ? (
        <ScrapeTable scrapes={scrapes} />
      ) : (
        <ReviewTable reviews={recentReviews} />
      )}
    </div>
  );
}

function ScrapeTable({ scrapes }: { scrapes: ProductDetail["scrapes"] }) {
  return (
    <section className="mt-4 overflow-x-auto rounded-2xl border border-slate-200 bg-white text-slate-900">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
          <tr>
            <th className="px-4 py-2 text-left">Created</th>
            <th className="px-4 py-2 text-left">Source</th>
            <th className="px-4 py-2 text-left">Status</th>
            <th className="px-4 py-2 text-left">Coverage</th>
            <th className="px-4 py-2 text-left">Reviews</th>
            <th className="px-4 py-2 text-left">Audit</th>
            <th className="px-4 py-2 text-left">Completed</th>
            <th className="px-4 py-2 text-left">Details</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {scrapes.length === 0 && (
            <tr>
              <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                No scrape attempts recorded for this product yet.
              </td>
            </tr>
          )}
          {scrapes.map((scrape) => (
            <tr key={scrape.id} className="align-top text-slate-700">
              <td className="whitespace-nowrap px-4 py-3 text-xs">
                {formatDateTime(scrape.createdAt)}
              </td>
              <td className="px-4 py-3 text-xs">{scrape.source}</td>
              <td className="px-4 py-3">
                <Pill className={statusClass(scrape.status)}>
                  {scrape.status}
                </Pill>
              </td>
              <td className="px-4 py-3 text-xs">
                {scrape.coverage ?? (scrape.source === "human" ? "manual" : "-")}
              </td>
              <td className="px-4 py-3 text-xs">
                {scrape.reviewCount}
                {scrape.flaggedCount > 0 && (
                  <span className="ml-1 text-amber-700">
                    ({scrape.flaggedCount} flagged)
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-xs">
                {scrape.auditRequestId ? (
                  <Link
                    to={`/admin/audits/${scrape.auditRequestId}`}
                    className="font-mono text-slate-700 underline-offset-4 hover:underline"
                  >
                    {scrape.auditRequestId.slice(0, 10)}
                  </Link>
                ) : (
                  "-"
                )}
              </td>
              <td className="whitespace-nowrap px-4 py-3 text-xs">
                {formatDateTime(scrape.completedAt ?? scrape.qaedAt)}
              </td>
              <td className="min-w-[260px] px-4 py-3 text-xs">
                <div className="space-y-1 text-slate-600">
                  {scrape.manualScraper && (
                    <div>
                      Scraper: {scrape.manualScraper.name || scrape.manualScraper.email}
                    </div>
                  )}
                  {scrape.errorCategory && (
                    <div className="text-red-700">
                      {scrape.errorCategory}: {scrape.errorMessage ?? "No message"}
                    </div>
                  )}
                  {scrape.notes && <div>{scrape.notes}</div>}
                  <Link
                    to={`/admin/scrapes/${scrape.id}`}
                    className="inline-flex text-slate-500 underline-offset-4 hover:text-slate-900 hover:underline"
                  >
                    Open scrape
                  </Link>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ReviewTable({ reviews }: { reviews: ProductDetail["recentReviews"] }) {
  return (
    <section className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900">
      <div className="divide-y divide-slate-100">
        {reviews.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-slate-500">
            No reviews saved for this ASIN yet.
          </div>
        )}
        {reviews.map((review) => {
          const flagged = review.classification?.flaggedForTakedown;
          return (
            <article key={review.id} className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{review.rating} star</span>
                  {flagged ? (
                    <Pill className="bg-amber-50 text-amber-700 ring-amber-200">
                      Flagged
                    </Pill>
                  ) : (
                    <Pill className="bg-slate-100 text-slate-700 ring-slate-200">
                      Clean
                    </Pill>
                  )}
                  {review.verifiedPurchase && (
                    <span className="text-xs text-slate-500">Verified purchase</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-500">
                  <Link
                    to={`/admin/audits/${review.auditRequest.id}`}
                    className="font-mono underline-offset-4 hover:text-slate-900 hover:underline"
                  >
                    {review.auditRequest.id.slice(0, 10)}
                  </Link>
                  {review.reviewUrl && (
                    <a
                      href={review.reviewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 hover:text-slate-900"
                    >
                      Amazon <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
              <h3 className="mt-3 text-sm font-semibold">
                {review.title || "Untitled review"}
              </h3>
              <p className="mt-1 text-sm leading-6 text-slate-700">{review.body}</p>
              {review.classification?.rationale && (
                <p className="mt-3 rounded-lg bg-slate-50 p-3 text-xs leading-5 text-slate-600">
                  {review.classification.rationale}
                </p>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

export default function AdminProductDetail() {
  return (
    <AdminGate>
      <AdminLayout>
        <AdminProductDetailInner />
      </AdminLayout>
    </AdminGate>
  );
}
